import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MetricsCards from '../components/analytics/MetricsCards';
import RevenueChart from '../components/revenue/RevenueChart';
import TopPagesTable from '../components/analytics/TopPagesTable';
// InsightsFeed removed - replaced with alerting system
import FilterBar from '../components/analytics/FilterBar';
import DateRangeSelector from '../components/DateRangeSelector';
import GA4ConnectionModal from '../components/GA4ConnectionModal';
import usePageMetadata from '../utils/usePageMetadata';
import './Analytics.css';

const Analytics = () => {
  usePageMetadata({
    title: 'Analytics Dashboard | The Ad Shop',
    description: 'Unified analytics dashboard for Google Analytics 4, AdSense, and Search Console data.'
  });

  const { token, user } = useAuth();
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [topPages, setTopPages] = useState([]);
  // Insights removed - replaced with alerting system
  const [deviceBreakdown, setDeviceBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [error, setError] = useState('');
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncError, setSyncError] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [noDataMessage, setNoDataMessage] = useState('');
  const [timeRange, setTimeRange] = useState(7); // days
  const [filters, setFilters] = useState({ device: 'all', trafficSource: 'all', geo: 'all' });
  const [topGeos, setTopGeos] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('pageviews');
  const [availableFilters, setAvailableFilters] = useState({ devices: [], trafficSources: [], geos: [] });
  const [showPropertySelection, setShowPropertySelection] = useState(false);
  const [availableProperties, setAvailableProperties] = useState([]);
  const [propertySelectionSiteId, setPropertySelectionSiteId] = useState(null);
  const [showGA4Modal, setShowGA4Modal] = useState(false);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [reconnectWarnings, setReconnectWarnings] = useState([]);
  const navigate = useNavigate();

  const plan = syncStatus?.plan;
  const retentionDays = plan?.dataRetentionDays || 90;

  useEffect(() => {
    // Only clamp if timeRange is a number (not a custom range object)
    if (typeof timeRange === 'number' && retentionDays > 0 && timeRange > retentionDays) {
      setTimeRange(retentionDays);
    }
  }, [retentionDays, timeRange]);

  const handleTimeRangeChange = useCallback(
    (range) => {
      // Handle custom date range object
      if (typeof range === 'object' && range?.custom) {
        setTimeRange(range);
      } else {
        // Handle numeric days
        const clamped = Math.max(0, Math.min(range, retentionDays));
        setTimeRange(clamped);
      }
    },
    [retentionDays]
  );

  const formatMinutesToHours = (minutes) => {
    if (minutes === null || minutes === undefined || Number.isNaN(minutes)) {
      return null;
    }
    const totalMinutes = Math.max(0, Math.round(minutes));
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}h ${mins.toString().padStart(2, '0')}m`;
  };

  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get('/api/sites', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sitesData = response.data.sites;
      setSites(sitesData);

      // Auto-select first site with GA4 connected
      const siteWithGA4 = sitesData.find(s => s.analyticsPropertyId);
      if (siteWithGA4) {
        setSelectedSite(siteWithGA4);
      } else if (sitesData.length > 0) {
        setSelectedSite(sitesData[0]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching sites:', error);
      setError('Failed to load sites');
      setLoading(false);
    }
  }, [token]);

  const fetchAnalyticsData = useCallback(async () => {
    if (!selectedSite) return;

    try {
      const params = new URLSearchParams({
        device: filters.device,
        trafficSource: filters.trafficSource,
        siteId: selectedSite.siteId
      });
      
      // Handle custom date range or numeric days
      if (typeof timeRange === 'object' && timeRange?.custom) {
        params.append('startDate', timeRange.startDate);
        params.append('endDate', timeRange.endDate);
      } else {
        params.append('days', timeRange);
      }
      
      if (filters.geo && filters.geo !== 'all') {
        params.append('geo', filters.geo);
      }
      
      console.log('Fetching analytics data for site:', selectedSite.siteId);
      const response = await axios.get(
        `/api/analytics-data/${selectedSite.siteId}/overview?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Analytics data response:', response.data);
      const summary = response.data.summary;
      const dailyData = response.data.dailyData || [];
      
      // Check if API explicitly says there's no data for this date range
      if (response.data.noDataMessage || response.data.needsSync) {
        // Clear all data and show the no-data message
        setAnalyticsData(null);
        setNoDataMessage(response.data.noDataMessage || 'No analytics data available. Click "Refresh Data" to run a scan and sync analytics from Google Analytics.');
        setDailyData([]);
        setTopPages([]);
        setDeviceBreakdown(null);
        setTopGeos([]);
        setAvailableFilters({ devices: [], trafficSources: [], geos: [] });
      } else {
        // Check if there's truly no data (summary is null or all zeros, and no daily data)
        const hasNoData = !summary || 
          (summary.users?.value === 0 && 
           summary.sessions?.value === 0 && 
           summary.pageviews?.value === 0 && 
           dailyData.length === 0);
        
        if (hasNoData) {
          setAnalyticsData(null);
          setNoDataMessage('No analytics data available. Click "Refresh Data" to run a scan and sync analytics from Google Analytics.');
        } else {
          setAnalyticsData(summary);
          setNoDataMessage(response.data.noDataMessage || '');
        }
        
        setDailyData(dailyData);
        setTopPages(response.data.topPages || []);
        setDeviceBreakdown(response.data.deviceBreakdown || null);
        setTopGeos(response.data.topGeos || []);
        setAvailableFilters(response.data.availableFilters || { devices: [], trafficSources: [], geos: [] });
      }
      setError('');
    } catch (error) {
      console.error('Error fetching analytics:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.status === 404) {
        setAnalyticsData(null);
        setNoDataMessage('No analytics data available. Click "Refresh Data" to run a scan and sync analytics from Google Analytics.');
      } else if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else {
        setError(error.response?.data?.message || 'Failed to load analytics data');
      }
    }
  }, [filters.device, filters.geo, filters.trafficSource, selectedSite, timeRange, token]);

  // fetchInsights and handleDismissInsight removed - insights no longer exist

  const checkSyncStatus = useCallback(async () => {
    if (!selectedSite) return;

    try {
      const response = await axios.get(
        `/api/analytics-data/${selectedSite.siteId}/sync-status?siteId=${selectedSite.siteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Sync status response:', response.data);
      setSyncStatus(response.data);
    } catch (error) {
      console.error('Error checking sync status:', error);
      // Default to allowing sync if status check fails
      setSyncStatus({ canSync: true, reason: 'Status check failed, allowing sync' });
    }
  }, [selectedSite, token]);

  useEffect(() => {
    fetchSites();
    
    // Check for property selection parameters in URL
    const urlParams = new URLSearchParams(window.location.search);
    const siteId = urlParams.get('siteId');
    const properties = urlParams.get('properties');
    
    if (siteId && properties) {
      try {
        const decodedProperties = JSON.parse(decodeURIComponent(properties));
        setAvailableProperties(decodedProperties);
        setPropertySelectionSiteId(siteId);
        setShowPropertySelection(true);
        
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (error) {
        console.error('Error parsing properties:', error);
      }
    }
  }, [fetchSites]);

  useEffect(() => {
    if (selectedSite) {
      fetchAnalyticsData();
      // Insights removed
      checkSyncStatus();
    }
  }, [checkSyncStatus, fetchAnalyticsData, selectedSite]);


  const handlePropertySelection = async (property) => {
    try {
      if (!propertySelectionSiteId) {
        setError('Site ID not found');
        return;
      }

      // Connect the selected property to the site
      await axios.post(
        `/api/connect-analytics`,
        {
          siteId: propertySelectionSiteId,
          propertyId: property.propertyId,
          propertyName: property.propertyName
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Close the modal and refresh sites
      setShowPropertySelection(false);
      await fetchSites();
      
    } catch (error) {
      console.error('Error connecting property:', error);
      setError('Failed to connect GA4 property. Please try again.');
    }
  };

  // Animate progress bar while syncing - loops continuously
  useEffect(() => {
    if (!syncing) {
      setSyncProgress(0);
      setSyncError(null);
      return;
    }

    // Start progress animation that loops continuously
    const progressInterval = setInterval(() => {
      setSyncProgress(prev => {
        // Increment by 2% every 500ms
        const next = prev + 2;
        // When we reach 100%, reset to 0 to loop again
        return next >= 100 ? 0 : next;
      });
    }, 500);

    return () => clearInterval(progressInterval);
  }, [syncing]);

  const handleSync = async () => {
    if (!selectedSite || syncing) return;

    // Check sync status before attempting sync
    if (syncStatus && !syncStatus.canSync) {
      const minutesText = syncStatus.minutesRemaining 
        ? (syncStatus.minutesRemaining >= 60 
          ? `${Math.floor(syncStatus.minutesRemaining / 60)} hour${Math.floor(syncStatus.minutesRemaining / 60) !== 1 ? 's' : ''} ${syncStatus.minutesRemaining % 60} minute${syncStatus.minutesRemaining % 60 !== 1 ? 's' : ''}`
          : `${syncStatus.minutesRemaining} minute${syncStatus.minutesRemaining !== 1 ? 's' : ''}`)
        : `${syncStatus.minutesRemaining} minutes`;
      alert(`Please wait ${minutesText} before syncing again.`);
      return;
    }

    // Show notice only when fetching more than 90 days of data
    // Syncs fetch data based on plan retention days, so check if retention > 90
    const retentionDays = plan?.dataRetentionDays || 90;
    if (retentionDays > 90) {
      const proceed = window.confirm(
        `Syncing ${retentionDays} days of data may take longer than usual. Please be patient while we fetch your historical data.\n\nDo you want to continue?`
      );
      if (!proceed) {
        return;
      }
    }

    setSyncing(true);
    setError('');
    setSyncError(null);
    setSyncProgress(0);

    try {
      console.log('Starting sync for site:', selectedSite.siteId);
      // Sync endpoint uses plan retention days, not the days parameter
      const syncResponse = await axios.post(
        `/api/analytics-data/${selectedSite.siteId}/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Sync response:', syncResponse.data);
      
      // Check for warnings about services that need reconnection
      // Filter out notices (informational messages) - only show actual warnings
      if (syncResponse.data?.warnings && syncResponse.data.warnings.length > 0) {
        const actualWarnings = syncResponse.data.warnings.filter(w => !w.isNotice);
        setReconnectWarnings(actualWarnings);
        
        // Show info notices (like long sync times) separately - just log them
        const notices = syncResponse.data.warnings.filter(w => w.isNotice);
        if (notices.length > 0) {
          notices.forEach(notice => {
            console.log('Sync notice:', notice.message);
          });
        }
      } else {
        setReconnectWarnings([]);
      }
      
      // Use the processed data from sync response if available
      if (syncResponse.data && syncResponse.data.processedData) {
        console.log('Using processed data from sync response');
        setAnalyticsData(syncResponse.data.processedData.summary);
        setDailyData(syncResponse.data.processedData.dailyData || []);
        setTopPages(syncResponse.data.processedData.topPages || []);
      } else {
        // Fallback to fetching data from overview endpoint
        console.log('Refreshing analytics data from overview endpoint...');
        await fetchAnalyticsData();
      }
      
      // Refresh sync status
      await checkSyncStatus();
      
      console.log('Sync completed successfully');
      // Small delay to show completion, then stop syncing (which stops the loop)
      setTimeout(() => {
        setSyncing(false);
        setSyncProgress(0);
      }, 500);
      
    } catch (error) {
      console.error('Sync error:', error);
      console.error('Error response:', error.response?.data);
      setSyncing(false);
      setSyncProgress(0);
      
      // Set sync error message
      let errorMessage = 'Sync failed. Please try again.';
      if (error.response?.status === 429) {
        errorMessage = error.response.data.message || 'Rate limit reached. Please wait before syncing again.';
        if (error.response.data) {
          setSyncStatus((prev) => ({
            ...(prev || {}),
            ...error.response.data
          }));
        }
      } else if (error.response?.status === 400 && error.response?.data?.code === 'NO_ANALYTICS_PROPERTY') {
        errorMessage = error.response.data.message || 'No Google Analytics 4 property connected. Please connect an analytics property before syncing data.';
        setNeedsReconnect(true);
      } else if (error.response?.status === 400 && error.response?.data?.code === 'GOOGLE_TOKEN_MISSING') {
        errorMessage = error.response.data.message || 'Google authentication is required. Please reconnect your Google account.';
        setNeedsReconnect(true);
      } else if (error.response?.status === 403 && error.response?.data?.requiresReconnect) {
        errorMessage = 'Your Google Analytics connection needs to be refreshed. Please reconnect your Google Analytics account to grant the required permissions.';
        setNeedsReconnect(true);
      } else if (error.response?.status === 401 && error.response?.data?.requiresReauth) {
        errorMessage = 'Google authentication expired. Please reconnect your Google account in the settings.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error during sync. Please try again in a few minutes.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      setSyncError(errorMessage);
      setError(errorMessage); // Also set main error for compatibility
    }
  };

  const chartSectionRef = React.useRef(null);

  const handleMetricSelect = (metric) => {
    setSelectedMetric(metric);
    // Scroll to chart after a brief delay to allow state update
    setTimeout(() => {
      if (chartSectionRef.current) {
        const elementPosition = chartSectionRef.current.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 200; // 200px offset to position higher
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const handleClearData = async () => {
    if (!selectedSite || clearing) return;

    if (!window.confirm('Are you sure you want to clear all analytics data for this site? This action cannot be undone.')) {
      return;
    }

    try {
      setClearing(true);
      setError('');
      
      await axios.get(`/api/analytics-data/${selectedSite.siteId}/overview?clear=true&siteId=${selectedSite.siteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Clear the current data display
      setAnalyticsData(null);
      setDailyData([]);
      setTopPages([]);
      // Insights removed
      
      alert('✅ Analytics data cleared successfully! Click "Refresh Data" to get fresh data from Google Analytics.');
      
    } catch (error) {
      console.error('Error clearing data:', error);
      setError('Failed to clear analytics data. Please try again.');
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <div className="analytics-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (sites.length === 0) {
    return (
      <div className="analytics-page">
        <div className="empty-state">
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M10 60L25 35L40 45L55 20L70 30" />
            <circle cx="25" cy="35" r="3" fill="currentColor" />
            <circle cx="40" cy="45" r="3" fill="currentColor" />
            <circle cx="55" cy="20" r="3" fill="currentColor" />
          </svg>
          <h2>No Sites Found</h2>
          <p>Add a site from your sites page to view analytics</p>
          <a href="/sites" className="btn-primary">Go to Sites</a>
        </div>
      </div>
    );
  }

  const hasGA4Connected = selectedSite?.analyticsPropertyId;
  
  // Debug logging
  console.log('Analytics page state:', {
    selectedSite,
    hasGA4Connected,
    analyticsPropertyId: selectedSite?.analyticsPropertyId,
    syncing,
    syncStatus
  });
  const hasData = analyticsData;
  const lastRefreshedLabel = syncStatus?.lastSync
    ? new Date(syncStatus.lastSync).toLocaleString()
    : null;
  const lastSyncText = syncing
    ? 'Refreshing now...'
    : lastRefreshedLabel
    ? `Last refreshed: ${lastRefreshedLabel}`
    : 'Last refreshed: Not synced yet';
  return (
    <div className="analytics-page">
      <header className="analytics-header">
        <div className="header-top">
          <div className="header-left">
            <div className="header-title-section">
              <h1>Analytics Dashboard</h1>
            </div>
            {selectedSite && (
              <div className="site-selector">
                <select 
                  value={selectedSite.siteId} 
                  onChange={(e) => {
                    const site = sites.find(s => s.siteId === e.target.value);
                    setSelectedSite(site);
                  }}
                  className="site-dropdown"
                >
                  {sites.map(site => (
                    <option key={site.siteId} value={site.siteId}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <DateRangeSelector 
              timeRange={timeRange} 
              onTimeRangeChange={handleTimeRangeChange}
              maxDays={retentionDays}
            />
            {hasData && (
              <FilterBar 
                onFiltersChange={setFilters} 
                currentFilters={filters}
                availableFilters={availableFilters}
              />
            )}
          </div>
          
          <div className="header-right">
            {(hasGA4Connected || selectedSite) && (
              <div className="sync-controls">
                <div className="sync-buttons">
                  <div className="sync-primary">
                    <button 
                      className="btn-sync"
                      onClick={handleSync}
                      disabled={syncing || (syncStatus && !syncStatus.canSync)}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0115.5-8.5l4 4M22 12.5a10 10 0 01-15.5 8.5l-4-4" />
                      </svg>
                      {syncing ? 'Syncing...' : 'Refresh Data'}
                    </button>
                    {syncing && (
                      <div className="sync-progress-bar">
                        <div 
                          className="sync-progress-fill" 
                          style={{ width: `${syncProgress}%` }}
                        ></div>
                      </div>
                    )}
                    {syncError && (
                      <div className="sync-error-message">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        {syncError}
                      </div>
                    )}
                    <span className="last-updated" aria-live="polite">
                      {lastSyncText}
                    </span>
                  </div>
                  <button 
                    className="btn-clear-data"
                    onClick={handleClearData}
                    disabled={clearing}
                    title="Clear all analytics data for this site"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M10 11v6M14 11v6" />
                    </svg>
                    {clearing ? 'Clearing...' : 'Clear Data'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        

        {syncStatus && !syncStatus.canSync && (
          <div className="rate-limit-warning">
            ⏱ Next refresh available in{' '}
            {formatMinutesToHours(syncStatus.minutesRemaining) || `${syncStatus.minutesRemaining} minutes`}
            {syncStatus.nextAvailableAt
              ? ` (${new Date(syncStatus.nextAvailableAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`
              : ''}
          </div>
        )}

        {reconnectWarnings.length > 0 && (
          <div className="warning-banner" style={{ 
            background: '#fff3cd', 
            border: '1px solid #ffc107', 
            borderRadius: '8px', 
            padding: '1rem', 
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" style={{ color: '#856404', flexShrink: 0 }}>
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div style={{ flex: 1 }}>
              {reconnectWarnings.map((warning, index) => (
                <div key={index} style={{ marginBottom: index < reconnectWarnings.length - 1 ? '0.5rem' : 0 }}>
                  {warning.message}
                </div>
              ))}
              <button 
                onClick={() => navigate('/sites')}
                style={{ 
                  marginTop: '0.5rem',
                  padding: '0.5rem 1rem', 
                  fontSize: '14px',
                  background: '#856404',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Go to Sites Page to Reconnect
              </button>
            </div>
            <button 
              onClick={() => setReconnectWarnings([])}
              style={{ 
                background: 'transparent', 
                border: 'none', 
                cursor: 'pointer', 
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center'
              }}
              aria-label="Dismiss warning"
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" style={{ color: '#856404' }}>
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}

        {error && (
          <div className="error-banner">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
            {needsReconnect && selectedSite && (
              <button 
                className="btn-reconnect"
                onClick={() => setShowGA4Modal(true)}
                style={{ marginLeft: '10px', padding: '5px 15px', fontSize: '14px' }}
              >
                Reconnect GA4
              </button>
            )}
          </div>
        )}

        {noDataMessage && (
          <div className="no-data-banner">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 12l2 2 4-4" />
              <circle cx="12" cy="12" r="10" />
            </svg>
            {noDataMessage}
          </div>
        )}
      </header>

      <main className="analytics-content">
        {!hasGA4Connected ? (
          <div className="setup-required">
            <div className="setup-card">
              <svg width="60" height="60" viewBox="0 0 60 60" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M30 10v40M10 30h40" />
                <circle cx="30" cy="30" r="20" />
              </svg>
              <h2>Connect Google Analytics 4</h2>
              <p>Connect your GA4 property to start viewing analytics</p>
              <a href="/sites" className="btn-primary">Connect GA4</a>
            </div>
          </div>
        ) : !hasData ? (
          <div className="no-data">
            <div className="no-data-card">
              <svg width="60" height="60" viewBox="0 0 60 60" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 50L20 30L30 35L40 15L50 25" />
              </svg>
              <h2>No Data Available</h2>
              <p>Click "Refresh Data" to pull your latest analytics from Google Analytics 4</p>
              <button className="btn-primary" onClick={handleSync} disabled={syncing}>
                {syncing ? 'Pulling Data...' : 'Refresh Data'}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Insights removed - replaced with alerting system */}

            <MetricsCards 
              data={analyticsData} 
              timeRange={timeRange} 
              selectedMetric={selectedMetric}
              onMetricSelect={handleMetricSelect}
              deviceBreakdown={deviceBreakdown}
              user={user}
              filters={filters}
              topGeos={topGeos}
              selectedSite={selectedSite}
              onGeoFilter={(geo) => {
                setFilters({ ...filters, geo });
                // When clicking a geo, switch to users metric to show user numbers by day
                if (geo !== 'all') {
                  setSelectedMetric('users');
                }
              }}
            />
            
            <div className="analytics-grid">
              <div className="chart-section" ref={chartSectionRef}>
                <RevenueChart
                  data={dailyData}
                  selectedMetric={selectedMetric}
                  filters={filters}
                  timeRange={timeRange}
                />
              </div>
              
              <div className="table-section">
                <TopPagesTable pages={topPages} siteId={selectedSite?.siteId} />
              </div>
            </div>
          </>
        )}
      </main>

      {/* Property Selection Modal */}
      {showPropertySelection && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Select GA4 Property</h2>
            <p>Choose which Google Analytics property to connect to this site:</p>
            <div className="property-list">
              {availableProperties.map((property, index) => (
                <div 
                  key={index} 
                  className="property-item"
                  onClick={() => handlePropertySelection(property)}
                >
                  <div className="property-name">{property.propertyName}</div>
                  <div className="property-id">ID: {property.propertyId}</div>
                  <div className="property-account">{property.accountName}</div>
                </div>
              ))}
            </div>
            <button 
              className="btn-secondary"
              onClick={() => setShowPropertySelection(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* GA4 Connection Modal for Reconnection */}
      {selectedSite && (
        <GA4ConnectionModal
          isOpen={showGA4Modal}
          onClose={() => {
            setShowGA4Modal(false);
            setNeedsReconnect(false);
            setError('');
          }}
          onConnect={async (propertyId, propertyName) => {
            try {
              // Update the site with new property
              await axios.post(
                `/api/sites/${selectedSite.siteId}/analytics`,
                { propertyId, propertyName },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              
              // Update selected site in local state
              const updatedSite = { ...selectedSite, analyticsPropertyId: propertyId, analyticsPropertyName: propertyName };
              setSelectedSite(updatedSite);
              setSites(sites.map(s => s.siteId === selectedSite.siteId ? updatedSite : s));
              
              // Close modal and clear errors
              setShowGA4Modal(false);
              setNeedsReconnect(false);
              setError('');
              
              // Refresh data
              await fetchAnalyticsData();
              
              alert('Google Analytics reconnected successfully!');
            } catch (error) {
              console.error('Error reconnecting GA4:', error);
              setError('Failed to reconnect Google Analytics. Please try again.');
            }
          }}
          site={selectedSite}
        />
      )}
    </div>
  );
};

export default Analytics;

